/**
 * Atlas PM · P8.1 任务模板 · 接口封装 + 结构映射
 *
 * 对接后端 TemplateController（apps/api/src/modules/template/task-template.controller.ts）：
 *   POST   /api/task-templates/from-task/:taskId   把任务存为模板
 *   GET    /api/task-templates                     可用模板列表（个人 + 公共）
 *   GET    /api/task-templates/:id                 模板详情（含 structure）
 *   DELETE /api/task-templates/:id                 删除模板
 *
 * ⚠️ 模板只承载「指标 + 要素」的结构，不含完成度/质量/负责人/日期/项目。
 *    故此处把 structure 映射为 TaskDesigner 的 MetricDraft 时，
 *    运行期字段（completionPct / qualityPct）一律置 0。
 */

import { ELEMENT_TYPE, type ElementType } from '@atlaspm/shared';
import { api, qs } from '../../api/client';
import type { Paginated } from '../../api/types';
import { draftKey, type MetricDraft } from './types';

/** 模板可见性：个人 / 公共 */
export type TemplateScope = 'PERSONAL' | 'PUBLIC';

export interface TemplateElement {
  name: string;
  description: string;
  elementType: string;
  isRequired: boolean;
  weight: number;
  requireAttachment: boolean;
  constraints: Record<string, unknown>;
  defaultValue: unknown;
  helpText: string | null;
  seq: number;
}

export interface TemplateMetric {
  name: string;
  metricType: string;
  unit: string | null;
  weight: number;
  seq: number;
  elements: TemplateElement[];
}

export interface TemplateStructure {
  metrics: TemplateMetric[];
}

export interface TaskTemplateListItem {
  id: string;
  name: string;
  description: string | null;
  scope: TemplateScope;
  categoryId: string | null;
  categoryName: string | null;
  metricCount: number;
  elementCount: number;
  createdByName: string | null;
  createdAt: string;
}

export interface TaskTemplateDetail extends TaskTemplateListItem {
  structure: TemplateStructure;
}

export interface SaveTemplateInput {
  name: string;
  description?: string;
  scope: TemplateScope;
}

const BASE = '/task-templates';

export const taskTemplateApi = {
  /** 把指定任务存为模板（结构由服务端提取） */
  fromTask: (taskId: string, input: SaveTemplateInput) =>
    api.post<TaskTemplateDetail>(`${BASE}/from-task/${taskId}`, input),

  /** 列出可用模板（scope 不传 = 自己的个人模板 + 全部公共模板） */
  list: (params: { scope?: TemplateScope; keyword?: string; page?: number; pageSize?: number } = {}) =>
    api.get<Paginated<TaskTemplateListItem>>(`${BASE}${qs(params)}`),

  /** 模板详情（含完整 structure） */
  detail: (id: string) => api.get<TaskTemplateDetail>(`${BASE}/${id}`),

  /** 删除模板 */
  remove: (id: string) => api.delete<{ ok: boolean; id: string }>(`${BASE}/${id}`),
};

/** 模板列表查询的 TanStack Query 键前缀（保存 / 删除后据此失效） */
export const TEMPLATE_QUERY_KEY = ['task-templates'] as const;
export const templateListKey = (params: {
  scope?: TemplateScope;
  keyword?: string;
  page?: number;
  pageSize?: number;
}) => ['task-templates', 'list', params] as const;

/**
 * 把模板结构映射为任务设计器的指标草稿。
 *
 * ⚠️ 关键点：
 *   · `weightTouched: true` —— 模板权重是权威结构，禁止「权重自动填满」覆盖；
 *   · 完成度 / 质量等运行期字段不填（模板本就不含），一律置 0；
 *   · 要素 elementType 原样带入（后端已保证合法取值范围）。
 */
export function structureToMetricDrafts(structure: TemplateStructure | null | undefined): MetricDraft[] {
  const metrics = structure?.metrics ?? [];
  return metrics.map((m) => ({
    key: draftKey('m'),
    name: m.name,
    metricType: m.metricType || 'PERCENT',
    unit: m.unit ?? '',
    weight: String(m.weight ?? 0),
    weightTouched: true,
    elements: (m.elements ?? []).map((e) => ({
      key: draftKey('e'),
      name: e.name,
      description: e.description ?? '',
      elementType: (e.elementType as ElementType) ?? ELEMENT_TYPE.TEXT,
      uiLabel: '',
      isRequired: e.isRequired ?? true,
      weight: e.weight ?? 0,
      completionPct: 0,
      qualityPct: 0,
      requireAttachment: e.requireAttachment ?? false,
      constraints: e.constraints,
      constraintText: e.helpText ?? '',
      helpText: e.helpText ?? undefined,
    })),
  }));
}
